import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useEffect } from 'react';
import { useGetRevenueMetricsMutation } from 'services/api/relifDashboard.api';

import useRevenueData from '../hooks/useRevenueData';
import IndustryLineChart from './charts/IndustryLineChart';
import MonthlyBarChart from './charts/MonthlyBarChart';
import ErrorState from './common/ErrorState';
import LoadingState from './common/LoadingState';
import StatCard from './StatCard';

interface RevenueDashboardProps {
  selectedYear: number;
}

const RevenueDashboard: React.FC<RevenueDashboardProps> = ({ selectedYear }) => {
  const [getRevenueMetrics, { data: revenueData, isLoading }] = useGetRevenueMetricsMutation();

  useEffect(() => {
    getRevenueMetrics({ year: selectedYear }).catch(() => {});
  }, [selectedYear, getRevenueMetrics]);

  const { monthlyRevenueData, revenueByIndustryData, totalLineData, monthLabels } = useRevenueData(
    revenueData,
    selectedYear
  );

  if (isLoading) {
    return <LoadingState />;
  }

  if (revenueData?.error) {
    return (
      <ErrorState
        title="Error al cargar datos de facturación"
        message={revenueData.error}
        additionalInfo="Intente de nuevo más tarde."
      />
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Facturación Mensual (USD)
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Facturación Anual"
            value={revenueData?.totals?.yearlyRevenue || 0}
            subtitle={`Año ${selectedYear}`}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Promedio Mensual"
            value={revenueData?.totals?.averageMonthlyGrowth || 0}
            subtitle="Crecimiento promedio mensual"
            color="secondary"
            isPercentage
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Industrias Activas"
            value={revenueData?.revenueByIndustry ? Object.keys(revenueData.revenueByIndustry).length : 0}
            subtitle="Con facturación registrada"
            color="success"
          />
        </Grid>
      </Grid>
      {monthlyRevenueData.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={12}>
            <MonthlyBarChart
              title={`Facturación Mensual Total - ${selectedYear}`}
              data={monthlyRevenueData}
              yAxisLabel="USD"
            />
          </Grid>
        </Grid>
      )}
      {revenueByIndustryData.length > 0 && (
        <Grid container spacing={3}>
          <Grid size={12}>
            <IndustryLineChart
              title={`Facturación por Industria - ${selectedYear}`}
              industryData={revenueByIndustryData}
              totalLineData={totalLineData}
              monthLabels={monthLabels}
              yAxisLabel="USD"
            />
          </Grid>
        </Grid>
      )}
      {monthlyRevenueData.length === 0 && !isLoading && (
        <Card sx={{ borderRadius: 3, p: 3 }}>
          <Typography
            variant="body1"
            align="center"
            sx={{
              color: 'text.secondary',
            }}
          >
            No hay datos de facturación disponibles para el año {selectedYear}
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default RevenueDashboard;
